import React from 'react';
import './MemoryGame.css';
import { getMediaPath } from 'app/functions';

export default class MemoryGame extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      selectedImages: [],
      matchedPairs: [],
      gameCompleted: false,
      moves: 0,
      score: 0,
      gameImages: []
    };
  }

  componentDidMount() {
    this.initializeGame();
  }

  componentDidUpdate(prevProps) {
    // Re-initialize game if images prop changes
    if (prevProps.images !== this.props.images || prevProps.selectedImagesNumber !== this.props.selectedImagesNumber) {
      this.initializeGame();
    }
  }

  initializeGame = () => {
    const {images, selectedImagesNumber} = this.props;
    
    if (images && images.length > 0 && selectedImagesNumber) {
      // Randomly select selectedImagesNumber images from the images array
      const shuffledImages = this.shuffleArray([...images]);
      const selectedImages = shuffledImages.slice(0, selectedImagesNumber);
      
      // Create pairs of selected images for the memory game
      let gameImages = [...selectedImages, ...selectedImages].map((image, index) => ({
        id: index,
        imageUrl: getMediaPath(image),
        isFlipped: false,
        isMatched: false,
        isPlaceholder: false
      }));
      
      // Fill to complete rows of 3 with specific placement
      const totalCards = gameImages.length;
      const cardsNeededForCompleteRows = Math.ceil(totalCards / 3) * 3;
      const additionalCardsNeeded = cardsNeededForCompleteRows - totalCards;
      
      if (additionalCardsNeeded > 0) {
        // Create placeholder cards
        const placeholderCards = [];
        for (let i = 0; i < additionalCardsNeeded; i++) {
          const placeholderCard = {
            id: totalCards + i,
            imageUrl: null,
            isFlipped: false,
            isMatched: false,
            isPlaceholder: true,
            placeholderColor: this.getRandomColor()
          };
          placeholderCards.push(placeholderCard);
        }
        
        // Insert placeholder cards at specific positions to ensure proper 3-column layout
        if (totalCards === 8) {
          // 4 pairs (8 cards) - need 1 more card for 3x3 grid
          // Add placeholder at position 4 (center of 3x3 grid)
          gameImages.splice(4, 0, placeholderCards[0]);
        } else if (totalCards === 10) {
          // 5 pairs (10 cards) - need 2 more cards for 4x3 grid
          // Add placeholders at positions 0 and 11 (corners)
          gameImages.splice(0, 0, placeholderCards[0]);
          gameImages.splice(11, 0, placeholderCards[1]);
        } else if (totalCards === 12) {
          // 6 pairs (12 cards) - already complete 4x3 grid, no placeholders needed
        } else if (totalCards === 14) {
          // 7 pairs (14 cards) - need 1 more card for 5x3 grid
          // Add placeholder at position 7 (center of 5x3 grid)
          gameImages.splice(7, 0, placeholderCards[0]);
        } else if (totalCards === 16) {
          // 8 pairs (16 cards) - need 2 more cards for 6x3 grid
          // Add placeholders at positions 0 and 17 (corners)
          gameImages.splice(0, 0, placeholderCards[0]);
          gameImages.splice(17, 0, placeholderCards[1]);
        } else {
          // For other cases, add at the end to complete the row
          gameImages.push(...placeholderCards);
        }
      }
      
      // Shuffle only the regular game cards, not the placeholder cards
      const regularCards = gameImages.filter(card => !card.isPlaceholder);
      const placeholderCards = gameImages.filter(card => card.isPlaceholder);
      const shuffledRegularCards = this.shuffleArray(regularCards);
      
      // Reconstruct the array with shuffled regular cards and fixed placeholder positions
      let finalGameImages = [];
      let regularCardIndex = 0;
      let placeholderCardIndex = 0;
      
      for (let i = 0; i < gameImages.length; i++) {
        if (gameImages[i].isPlaceholder) {
          finalGameImages.push(placeholderCards[placeholderCardIndex]);
          placeholderCardIndex++;
        } else {
          finalGameImages.push(shuffledRegularCards[regularCardIndex]);
          regularCardIndex++;
        }
      }
      
      this.setState({
        gameImages: finalGameImages,
        selectedImages: [],
        matchedPairs: [],
        gameCompleted: false,
        moves: 0,
        score: 0
      });
    }
  }

  shuffleArray = (array) => {
    const newArray = [...array];
    for (let i = newArray.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
    }
    return newArray;
  }

  getRandomColor = () => {
    const colors = ['#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7', '#DDA0DD', '#98D8C8', '#F7DC6F'];
    return colors[Math.floor(Math.random() * colors.length)];
  }

  handleImagePress = (imageId) => {
    const {gameImages, selectedImages} = this.state;
    
    // Don't allow clicking on already matched, flipped, or placeholder images
    const image = gameImages.find(img => img.id === imageId);
    
    if (!image || image.isMatched || image.isFlipped || image.isPlaceholder || selectedImages.length >= 2) {
      return;
    }

    // Flip the selected image
    const updatedImages = gameImages.map(img => 
      img.id === imageId ? {...img, isFlipped: true} : img
    );

    const newSelectedImages = [...selectedImages, imageId];

    this.setState({
      gameImages: updatedImages,
      selectedImages: newSelectedImages
    });

    // Check if we have two images selected
    if (newSelectedImages.length === 2) {
      this.setState(prevState => ({moves: prevState.moves + 1}));
      
      setTimeout(() => {
        this.checkForMatch(newSelectedImages);
      }, 1000);
    }
  }

  checkForMatch = (selectedIds) => {
    const {gameImages} = this.state;
    const [firstId, secondId] = selectedIds;
    const firstImage = gameImages.find(img => img.id === firstId);
    const secondImage = gameImages.find(img => img.id === secondId);

    if (firstImage && secondImage && firstImage.imageUrl === secondImage.imageUrl) {
      // Match found
      const updatedImages = gameImages.map(img => 
        selectedIds.includes(img.id) ? {...img, isMatched: true, isFlipped: true} : img
      );

      this.setState(prevState => {
        const newMatchedPairs = [...prevState.matchedPairs, firstImage.imageUrl];
        const newScore = prevState.score + (firstImage.isBonus || secondImage.isBonus ? 15 : 10);
        
        // Check if game is completed (only count regular game cards, not placeholders)
        const regularGameCards = gameImages.filter(img => !img.isPlaceholder && !img.isBonus);
        const totalPairsNeeded = regularGameCards.length / 2;
        
        if (newMatchedPairs.length === totalPairsNeeded) {
          // Call the onGameCompleted callback if provided
          const {onGameCompleted} = this.props;
          if (typeof onGameCompleted === 'function') {
            onGameCompleted(prevState.moves + 1);
          }
        }

        return {
          gameImages: updatedImages,
          selectedImages: [],
          matchedPairs: newMatchedPairs,
          score: newScore,
          gameCompleted: newMatchedPairs.length === totalPairsNeeded
        };
      });
    } else {
      // No match, flip back
      const updatedImages = gameImages.map(img => 
        selectedIds.includes(img.id) ? {...img, isFlipped: false} : img
      );

      this.setState({
        gameImages: updatedImages,
        selectedImages: []
      });
    }
  }

  resetGame = () => {
    this.initializeGame();
  }

  getGridConfig = (totalCards) => {
    // Dynamic grid configuration based on number of cards - always 3 columns
    const modalWidth = Math.min(window.innerWidth * 0.98 - 30, 600); // Account for padding and max width
    const availableWidth = modalWidth - 16; // Account for grid padding
    
    // Calculate card size based on available width and number of rows
    const rows = Math.ceil(totalCards / 3);
    const isLargeScreen = window.innerWidth >= 1200;
    const gapSize = isLargeScreen ? 0 : 4; // No gap on large screens
    const maxCardSize = Math.min((availableWidth - gapSize) / 3, 130); // 3 columns, account for gap, even larger max size
    
    
    return {
      columns: 3,
      cardSize: maxCardSize,
      rows: rows
    };
  }

  render() {
    const {
      placeholderImage, 
      addedDefaultCardsImage, 
      selectedImagesNumber,
      memoryGameScoreText,
      memoryGameMovesText,
      memoryGamePopupBottomText
    } = this.props;
    const {gameImages, gameCompleted, moves, score} = this.state;
  
    // Calculate dynamic grid layout based on number of cards
    const totalCards = gameImages ? gameImages.length : 0;
    const gridConfig = this.getGridConfig(totalCards);

    if (gameCompleted) {
      return null; // Let parent modal handle completion display
    }

    return (
      <div className="memory-game-container">
        <div className="stats-container">
          <span className="stats-text">{memoryGameMovesText || 'מהלכים'}: {moves}</span>
          <span className="stats-text">{memoryGameScoreText || 'ניקוד'}: {score}</span>
        </div>
        
        <div className="images-grid-container">
          <div className="images-grid">
            {gameImages && gameImages.map((image) => (
              <div
                key={image.id}
                className={`image-container ${image.isFlipped ? 'image-flipped' : ''} ${image.isBonus ? 'bonus-card' : ''} ${image.isPlaceholder ? 'placeholder-card' : ''}`}
                style={{
                  width: gridConfig.cardSize,
                  height: gridConfig.cardSize
                }}
                onClick={image.isPlaceholder ? null : () => this.handleImagePress(image.id)}
              >
                {image.isFlipped ? (
                  <img
                    src={getMediaPath(image.imageUrl)}
                    className="game-image"
                    alt="game card"
                  />
                ) : (
                  <div className="card-back">
                    {image.isPlaceholder ? (
                      addedDefaultCardsImage ? (
                        <img
                          src={getMediaPath(addedDefaultCardsImage)}
                          className="game-image"
                          alt="placeholder"
                        />
                      ) : (
                        <div className="placeholder-content" style={{backgroundColor: image.placeholderColor}}>
                          <span className="placeholder-text">•</span>
                        </div>
                      )
                    ) : placeholderImage ? (
                      <img
                        src={getMediaPath(placeholderImage)}
                        className="game-image"
                        alt="card back"
                      />
                    ) : (
                      <span className="card-back-text">?</span>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
        
        <div className="challenge-text">
          {memoryGamePopupBottomText || `השיא שלנו הוא ${selectedImagesNumber + 4} מהלכים - נראה אתכם מנצחים אותנו`}
        </div>
      </div>
    );
  }
} 